import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { apiInstanceFetch } from "../../../util/api";
import { setToast } from "../../../util/toast";
import Button from "../../extra/Button";
import Table from "../../extra/Table";
import Pagination from "../../extra/Pagination";
import Skeleton from "react-loading-skeleton";
import { colors } from "../../../util/SkeletonColor";
import "react-loading-skeleton/dist/skeleton.css";

const REASONS = [
  "Promotional grant",
  "Bug compensation",
  "Refund (manual)",
  "Support resolution",
  "Testing",
  "Other",
];

const AdjustCoins = () => {
  const admin = useSelector((s) => s.admin?.admin) || {};

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [direction, setDirection] = useState("credit");
  const [coins, setCoins] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState({ coins: "", note: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchUsers = async (q = "") => {
    try {
      setLoading(true);
      const res = await apiInstanceFetch.get(
        `rewardWallet/searchUsers?search=${encodeURIComponent(q)}`
      );
      if (res.status) setUsers(res.users || []);
    } catch (err) {
      console.error("searchUsers:", err);
      setToast("error", "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
    fetchUsers(value.trim());
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setDirection("credit");
    setCoins("");
    setReason(REASONS[0]);
    setNotes("");
    setError({ coins: "", note: "" });
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setCoins("");
    setNotes("");
    setError({ coins: "", note: "" });
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      setToast("error", "Please select a user first");
      return;
    }
    const qty = parseInt(coins, 10);
    const newErr = { coins: "", note: "" };
    if (!Number.isInteger(qty) || qty <= 0) {
      newErr.coins = "Enter a positive whole number";
    }
    if (!notes.trim() && reason === "Other") {
      newErr.note = "Notes required when reason is Other";
    }
    if (newErr.coins || newErr.note) {
      setError(newErr);
      return;
    }

    const amount = direction === "debit" ? -qty : qty;
    const note = notes.trim() ? `${reason} — ${notes.trim()}` : reason;
    const adminId = admin?._id || "admin";

    const who =
      selectedUser.fullName ||
      `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() ||
      (selectedUser.uniqueId ? `ID ${selectedUser.uniqueId}` : selectedUser.email);
    const confirmMsg =
      direction === "debit"
        ? `Remove ${qty} coins from ${who}? This is logged to the ledger.`
        : `Grant ${qty} coins to ${who}? This is logged to the ledger.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setSubmitting(true);
      // apiInstanceFetch.post already JSON.stringifies the body — pass a plain
      // object (double-stringify made Express reject it: "Failed to apply").
      const res = await apiInstanceFetch.post(
        "rewardWallet/adminAdjust",
        {
          userId: selectedUser._id,
          amount,
          note,
          adminId,
        }
      );
      if (res.status) {
        setToast("success", res.message || "Adjustment applied.");
        handleCancel();
        fetchUsers(search.trim());
      } else {
        setToast("error", res.message || "Failed to apply adjustment");
      }
    } catch (err) {
      console.error("adminAdjust:", err);
      setToast("error", "Failed to apply adjustment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => setCurrentPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event), 10);
    setCurrentPage(1);
  };

  const getPaginatedData = () => {
    const start = (currentPage - 1) * rowsPerPage;
    return users.slice(start, start + rowsPerPage);
  };

  const mapData = [
    {
      Header: "No",
      width: "20px",
      Cell: ({ index }) => (
        <span>{(currentPage - 1) * rowsPerPage + index + 1}</span>
      ),
    },
    {
      Header: "User",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="position-relative">
            {loading ? (
              <Skeleton
                height={42}
                width={42}
                style={{ borderRadius: "90px" }}
                baseColor={colors?.baseColor}
                highlightColor={colors?.highlightColor}
              />
            ) : (
              <img
                src={row?.image}
                height={45}
                width={45}
                alt=""
                style={{ borderRadius: "90px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/dummy.png";
                }}
              />
            )}
          </div>
          <span className="fw-normal ms-3 text-start">
            <b className="fw-normal text-capitalize">
              {row.fullName ||
                `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
                "Unknown"}
            </b>
            <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
              {row.email || "-"}
            </p>
          </span>
        </div>
      ),
    },
    {
      Header: "Unique ID",
      Cell: ({ row }) => <span>{row.uniqueId || "-"}</span>,
    },
    {
      Header: "Reward Balance",
      Cell: ({ row }) => (
        <span
          className="badge"
          style={{
            backgroundColor: "#F0C420",
            color: "#000",
            padding: "8px 12px",
            borderRadius: "5px",
            fontSize: "14px",
          }}
          title={`≈ $${((row.balance || 0) / 1000).toFixed(2)}`}
        >
          {(row.balance || 0).toLocaleString()} Coins
        </span>
      ),
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <Button
          btnName="Adjust"
          btnColor="btnBlackPrime text-light"
          style={{ borderRadius: "5px", padding: "8px 16px" }}
          onClick={() => handleSelectUser(row)}
        />
      ),
    },
  ];

  const previewDelta = coins && parseInt(coins, 10) > 0
    ? (direction === "debit" ? -1 : 1) * parseInt(coins, 10)
    : 0;
  const newBalance = selectedUser
    ? (selectedUser.balance || 0) + previewDelta
    : 0;

  return (
    <div className="mainSellerTable">
      <div className="sellerTable">
        <div className="sellerHeader primeHeader">
          <div className="row align-items-center">
            <div className="col-6">
              <h4 className="mb-0">Adjust User Coins</h4>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                Manual credits or debits to a user's reward wallet. All
                adjustments are logged to the Coin Ledger.
              </p>
            </div>
            <div className="col-6">
              <div className="d-flex justify-content-end">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email, ID, or phone..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    padding: "10px 15px",
                    borderRadius: "5px",
                    maxWidth: "300px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {selectedUser && (
          <div
            className="mb-4 p-4"
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "10px",
              border: "1px solid #dee2e6",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" style={{ color: "#333" }}>
                Adjust:{" "}
                {selectedUser.fullName ||
                  `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
              </h5>
              <button
                className="btn btn-outline-secondary"
                onClick={handleCancel}
                style={{ padding: "5px 15px" }}
              >
                <i className="fa-solid fa-xmark"></i> Cancel
              </button>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <div
                  className="p-3"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={selectedUser.image}
                      height={60}
                      width={60}
                      alt=""
                      style={{ borderRadius: "50%", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/dummy.png";
                      }}
                    />
                    <div className="ms-3">
                      <p className="mb-0 fw-bold" style={{ color: "#333" }}>
                        {selectedUser.fullName ||
                          `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
                        ID: {selectedUser.uniqueId || "-"}
                        {selectedUser.email &&
                        !selectedUser.email.includes("privaterelay.appleid.com")
                          ? ` · ${selectedUser.email}`
                          : ""}
                      </p>
                      <p
                        className="mb-0"
                        style={{
                          fontSize: "12px",
                          color: "#F0C420",
                          fontWeight: "bold",
                        }}
                      >
                        Current: {(selectedUser.balance || 0).toLocaleString()} coins
                        {" "}
                        <span className="text-muted fw-normal">
                          (≈ ${((selectedUser.balance || 0) / 1000).toFixed(2)})
                        </span>
                      </p>
                      {selectedUser.walletStatus &&
                        selectedUser.walletStatus !== "active" &&
                        selectedUser.walletStatus !== "none" && (
                          <p
                            className="mb-0"
                            style={{
                              fontSize: "11px",
                              color: "#EF4444",
                              fontWeight: "bold",
                            }}
                          >
                            ⚠ Wallet: {selectedUser.walletStatus}
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <label className="mb-2 fw-bold" style={{ color: "#333" }}>
                  Direction
                </label>
                <div className="d-flex" style={{ gap: "8px" }}>
                  <button
                    type="button"
                    onClick={() => setDirection("credit")}
                    className={`btn flex-fill ${
                      direction === "credit"
                        ? "btn-success"
                        : "btn-outline-success"
                    }`}
                    style={{ padding: "10px" }}
                  >
                    + Credit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirection("debit")}
                    className={`btn flex-fill ${
                      direction === "debit" ? "btn-danger" : "btn-outline-danger"
                    }`}
                    style={{ padding: "10px" }}
                  >
                    − Debit
                  </button>
                </div>
              </div>

              <div className="col-md-3">
                <label className="mb-2 fw-bold" style={{ color: "#333" }}>
                  Amount (coins)
                </label>
                <input
                  type="number"
                  min="1"
                  className={`form-control ${error.coins ? "is-invalid" : ""}`}
                  value={coins}
                  placeholder="e.g. 1000"
                  onChange={(e) => {
                    setCoins(e.target.value);
                    if (e.target.value && parseInt(e.target.value, 10) > 0) {
                      setError((p) => ({ ...p, coins: "" }));
                    }
                  }}
                  style={{ padding: "10px", borderRadius: "5px" }}
                />
                {error.coins && (
                  <div className="invalid-feedback d-block">{error.coins}</div>
                )}
                {previewDelta !== 0 && (
                  <small
                    className="text-muted d-block mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    New balance:{" "}
                    <b style={{ color: newBalance < 0 ? "#EF4444" : "#333" }}>
                      {newBalance.toLocaleString()}
                    </b>{" "}
                    (≈ ${(newBalance / 1000).toFixed(2)})
                  </small>
                )}
              </div>

              <div className="col-md-2">
                <label className="mb-2 fw-bold" style={{ color: "#333" }}>
                  Reason
                </label>
                <select
                  className="form-control"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ padding: "10px", borderRadius: "5px" }}
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-12">
                <label className="mb-2" style={{ color: "#333" }}>
                  Notes {reason === "Other" ? "(required)" : "(optional)"}
                </label>
                <input
                  type="text"
                  className={`form-control ${error.note ? "is-invalid" : ""}`}
                  value={notes}
                  placeholder="Ticket #, context, link, etc. (stored in ledger metadata)"
                  onChange={(e) => {
                    setNotes(e.target.value);
                    if (e.target.value.trim()) {
                      setError((p) => ({ ...p, note: "" }));
                    }
                  }}
                  style={{ padding: "10px", borderRadius: "5px" }}
                />
                {error.note && (
                  <div className="invalid-feedback d-block">{error.note}</div>
                )}
              </div>
            </div>

            <div className="mt-3 d-flex justify-content-end">
              <Button
                btnName={
                  submitting
                    ? "Applying..."
                    : direction === "debit"
                    ? "Remove Coins"
                    : "Grant Coins"
                }
                btnColor={
                  direction === "debit"
                    ? "btn-danger text-light"
                    : "btnBlackPrime text-light"
                }
                style={{ borderRadius: "5px", padding: "10px 30px" }}
                onClick={handleSubmit}
                disabled={submitting}
              />
            </div>
          </div>
        )}

        <div className="sellerMain">
          <div className="tableMain mt-2">
            <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
              Select a user to credit or debit their reward wallet
            </p>
            <Table
              data={getPaginatedData()}
              mapData={mapData}
              serverPerPage={rowsPerPage}
              serverPage={currentPage}
              type={"client"}
            />
            <Pagination
              component="div"
              count={users?.length}
              type={"client"}
              onPageChange={handleChangePage}
              serverPerPage={rowsPerPage}
              totalData={users?.length}
              serverPage={currentPage}
              setCurrentPage={setCurrentPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
        <div className="sellerFooter primeFooter"></div>
      </div>
    </div>
  );
};

export default AdjustCoins;
